import { noop } from 'es-toolkit/compat';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { basePath } from 'shared/utils/RouterUtils';
import { logWarning } from '../Logger';

function isDocumentReadyForServiceWorker(): boolean {
  if (typeof document === 'undefined') return false;
  const { readyState } = document;
  return readyState === 'interactive' || readyState === 'complete';
}

function registerServiceWorkerWhenReady(): void {
  if (!('serviceWorker' in navigator) || isWebView) return;
  if (!isDocumentReadyForServiceWorker()) {
    const onReady = (): void => {
      document.removeEventListener('DOMContentLoaded', onReady);
      window.removeEventListener('load', onReady);
      registerServiceWorker();
    };
    document.addEventListener('DOMContentLoaded', onReady);
    window.addEventListener('load', onReady);
    return;
  }
  registerServiceWorker();
}

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  const serviceWorkerPath = `${basePath}/service-worker-messaging.js`;

  Promise.resolve()
    .then(() => navigator.serviceWorker.getRegistrations())
    .then((registrations) => {
      registrations.forEach((registration) => {
        const scriptURL = registration.active?.scriptURL || '';
        if (scriptURL && !scriptURL.endsWith(serviceWorkerPath)) {
          registration.unregister().catch(noop);
        }
      });
    })
    .catch((error: Error) => {
      if (error && error.name === 'InvalidStateError') return;
      logWarning('BrowserNotification', {
        error: 'Service worker getRegistrations failed',
        details: error.message,
      });
    });

  navigator.serviceWorker
    .register(serviceWorkerPath)
    .then((registration) => {
      if (registration.active) {
        registration.update().catch((error) => {
          logWarning('BrowserNotification', {
            error: 'Service worker update failed',
            details: error instanceof Error ? error.message : String(error),
          });
        });
      }
    })
    .catch((error: unknown) => {
      if (error instanceof Error && error.name === 'InvalidStateError') return;
      logWarning('BrowserNotification', {
        error: 'Service worker registration failed',
        details: error instanceof Error ? error.message : String(error),
      });
    });
}

registerServiceWorkerWhenReady();

async function showServiceWorkerNotification(
  title: string,
  options?: NotificationOptions,
): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return Promise.reject(
      new Error('Failed to show service worker notification'),
    );
  }
  if (!isDocumentReadyForServiceWorker()) {
    return Promise.reject(
      new Error('Document is not ready for service worker'),
    );
  }
  return navigator.serviceWorker.ready
    .then((registration) => registration.showNotification(title, options))
    .catch((error: unknown) => {
      if (error instanceof Error && error.name === 'InvalidStateError') {
        return Promise.reject(
          new Error('Document is in an invalid state for notifications'),
        );
      }
      return Promise.reject(error);
    });
}

export async function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    // Support deprecated callback syntax
    // https://developer.mozilla.org/en-US/docs/Web/API/Notification/requestPermission#syntax
    return Promise.resolve(Notification.requestPermission());
  }

  return logWarning('BrowserNotification', {
    error: 'Notification is not supported',
  });
}

function showNotification(
  title: string,
  clickAction: string,
  options?: NotificationOptions,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const notification = new Notification(title, options);

    notification.addEventListener('click', () => {
      window.focus();
      notification.close();
      openExternalURL(clickAction);
    });

    notification.addEventListener('error', () => {
      reject(new Error('Failed to show notification'));
    });

    notification.addEventListener('show', () => {
      resolve();
    });
  });
}

export async function showBrowserNotification(
  tag: string,
  title: string,
  body: string,
  clickAction: string,
) {
  if (!('Notification' in window)) {
    throw new Error('Not supported');
  }

  if (Notification.permission !== 'granted') {
    void requestNotificationPermission().catch(noop);
    throw new Error('No required permission');
  }

  const { href: icon } = new URL(
    `${basePath}/logo.png`,
    window.location.origin,
  );
  const notificationOptions: NotificationOptions = { body, tag, icon };

  try {
    await showServiceWorkerNotification(title, {
      ...notificationOptions,
      data: { click_action: clickAction },
    });
  } catch (error: unknown) {
    return showNotification(title, clickAction, notificationOptions);
  }
}
